import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { startOfMonth, endOfMonth, format } from "date-fns";

export interface UserAttendanceData {
  attendanceDates: Set<string>; // ISO date strings (YYYY-MM-DD)
  attendanceByDate: Map<string, string[]>; // date -> array of times
}

interface UseUserAttendanceOptions {
  userId: string;
  gymId: string;
  month: Date;
  enabled?: boolean;
}

export const useUserAttendance = ({
  userId,
  gymId,
  month,
  enabled = true,
}: UseUserAttendanceOptions) => {
  const monthStart = startOfMonth(month);
  const monthEnd = endOfMonth(month);

  return useQuery({
    queryKey: ["user-attendance", userId, gymId, format(monthStart, "yyyy-MM")],
    queryFn: async (): Promise<UserAttendanceData> => {
      const { data, error } = await supabase
        .from("gym_attendance_logs")
        .select("check_in_at")
        .eq("user_id", userId)
        .eq("gym_id", gymId)
        .gte("check_in_at", monthStart.toISOString())
        .lte("check_in_at", monthEnd.toISOString())
        .order("check_in_at", { ascending: true });

      if (error) throw error;

      const attendanceDates = new Set<string>();
      const attendanceByDate = new Map<string, string[]>();

      (data || []).forEach(record => {
        const checkInDate = new Date(record.check_in_at);
        const dateKey = format(checkInDate, "yyyy-MM-dd");
        const timeKey = format(checkInDate, "HH:mm:ss");

        attendanceDates.add(dateKey);

        if (!attendanceByDate.has(dateKey)) {
          attendanceByDate.set(dateKey, []);
        }
        attendanceByDate.get(dateKey)!.push(timeKey);
      });

      return { attendanceDates, attendanceByDate };
    },
    enabled: enabled && !!userId && !!gymId,
  });
};
